import React from 'react';
import { Link } from 'react-router-dom';

function Support() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-black mb-4">Life Coach Assist — Support</h1>
          <p className="text-2xl text-gray-700 font-semibold">We're here to support your journey</p>
        </div>

        {/* Introduction */}
        <div className="mb-12">
          <p className="text-lg text-gray-700 leading-relaxed">
            Life Coach Assist is designed to help you slow down, reflect, and gain clarity through supportive AI-guided conversations. If you have questions, need help, or run into an issue, we're here to help.
          </p>
        </div>

        <hr className="border-gray-300 my-8" />

        {/* Contact Support */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-black mb-4">Contact Support</h2>
          <p className="text-lg text-gray-700 mb-4">
            For any questions or support requests, please email:
          </p>
          <p className="text-lg mb-4">
            <a 
              href="mailto:support@lifecoachassist.ai" 
              className="text-blue-600 hover:underline font-bold text-xl"
            >
              support@lifecoachassist.ai
            </a>
          </p>
          <p className="text-lg text-gray-700">
            We typically respond within <strong>one to two business days</strong>.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        {/* Using Life Coach Assist */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-black mb-4">Using Life Coach Assist</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Life Coach Assist provides AI-powered coaching conversations intended to support personal reflection, insight, and growth. Responses are generated based on the information you choose to share and are designed to be thoughtful, supportive, and non-judgmental.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Life Coach Assist is <strong>not a substitute for professional medical, mental health, or legal advice</strong>.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        {/* Accounts, Privacy, and Data */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-black mb-4">Accounts, Privacy, and Data</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Creating an account allows Life Coach Assist to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-lg text-gray-700 mb-4 ml-4">
            <li>Securely associate conversations with you</li>
            <li>Provide continuity across sessions</li>
            <li>Protect your information from unauthorized access</li>
          </ul>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            We take privacy seriously and do not sell your personal information.
          </p>
          <p className="text-lg text-gray-700">
            You can review our Privacy Policy here:{' '}
            <Link to="/privacy-policy" className="text-blue-600 hover:underline font-semibold">
              Privacy Policy
            </Link>
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        {/* Account Deletion */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-black mb-4">Account Deletion</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            You may request <strong>permanent deletion</strong> of your Life Coach Assist account and associated data at any time.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            To delete your account:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-lg text-gray-700 mb-4 ml-4">
            <li>Email <a href="mailto:support@lifecoachassist.ai" className="text-blue-600 hover:underline font-semibold">support@lifecoachassist.ai</a></li>
            <li>Use the subject line <strong>"Account Deletion Request"</strong></li>
            <li>Include the email address associated with your account</li>
          </ol>
          <p className="text-lg text-gray-700 leading-relaxed">
            Once confirmed, your account and data will be permanently deleted and cannot be recovered.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        {/* Troubleshooting */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-black mb-4">Troubleshooting</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            If the app isn't working as expected, try:
          </p>
          <ul className="list-disc list-inside space-y-2 text-lg text-gray-700 mb-4 ml-4">
            <li>Confirming you're using the latest app version</li>
            <li>Checking your internet connection</li>
            <li>Closing and reopening the app</li>
          </ul>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            If the issue continues, please email support and include:
          </p>
          <ul className="list-disc list-inside space-y-2 text-lg text-gray-700 ml-4">
            <li>Your device model</li>
            <li>iOS version</li>
            <li>App version</li>
            <li>A brief description of the issue</li>
          </ul>
        </section>

        <hr className="border-gray-300 my-8" />

        {/* Company Information */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-black mb-4">Company Information</h2>
          <p className="text-lg text-gray-700">
            Life Coach Assist<br />
            United States
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        {/* Footer Note */}
        <p className="text-base text-gray-600 italic">
          This support page is accessible from the App Store listing and provides assistance with app usage, privacy, and account management.
        </p>

        {/* Back to Home Link */}
        <div className="mt-12 text-center">
          <Link 
            to="/" 
            className="inline-block bg-[#FCF171] text-black px-8 py-3 rounded-lg font-bold text-lg hover:bg-[#F5EA4A] transition-colors duration-300"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Support;
